import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import AddRemoveArtist from '../../components/Common/AddRemoveArtist';
import SetupShowcase from './SetupShowcase';
import RestUtilities from '../../services/RestUtilities';
import { dbDayFormat } from '../../Constants';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Container,
} from '@mui/material';
import { loadingState, toastState } from '../../state/RecoilState';
import { useSetRecoilState } from 'recoil';

export default function EditShowcase() {
  const { city } = useParams();
  const setLoading = useSetRecoilState(loadingState);
  const setToast = useSetRecoilState(toastState);
  const [activeStep, setActiveStep] = useState(0);
  const [allArtists, setAllArtists] = useState([]);
  const [lineUp, setLineUp] = useState([]);
  const [date, setDate] = useState(dayjs(new Date()));
  const [time, setTime] = useState('');
  const [venue, setVenue] = useState('');
  const [location, setLocation] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [description, setDescription] = useState('');
  const [, setIsTouched] = useState(false);
  const steps = ['Setup Showcase', 'Set Lineup', 'Finished'];
  const [editor, setEditor] = useState(undefined);

  useEffect(() => {
    const getAllData = async () => {
      setLoading(true);
      const res = await RestUtilities.get(`/api/showcase/Get/${city}`);
      const { showcase, artists, lineUp } = res.content;
      const newDate = dayjs(showcase.date);
      setAllArtists(artists);
      setLineUp(lineUp);
      setDate(newDate);
      setVenue(showcase.venue ?? '');
      setLocation(showcase.location);
      setMetaDesc(showcase.metaDesc);
      setMetaKeywords(showcase.metaKeywords);
      setTime(showcase.time);
      setDescription(showcase.description);
      setMetaTitle(showcase.metaTitle);
      setActiveStep(0);
      setToast('Showcase Loaded');
      setLoading(false);
    };

    getAllData();
  }, [city, setLoading, setToast]);

  const handleNext = async () => {
    if (activeStep === 1) {
      await handleSave();
    } else if (activeStep === 0) {
      const newHtml = editor.getHTML();
      setDescription(newHtml);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSave = async () => {
    const showcase = {
      city,
      date,
      venue,
      location,
      metaDesc,
      metaKeywords,
      metaTitle,
      time,
      description,
    };
    const showcaseUpdate = {
      showcase,
      lineUp,
    };
    const res = await RestUtilities.post(
      `/api/showcase/update/`,
      showcaseUpdate
    );
    if (res.ok) {
      var updatedShowcase = res.content;
      setDate(dayjs(updatedShowcase.date).format(dbDayFormat));
      setVenue(updatedShowcase.venue);
      setLocation(updatedShowcase.location);
      setMetaDesc(updatedShowcase.metaDesc);
      setMetaKeywords(updatedShowcase.metaKeywords);
      setTime(updatedShowcase.time);
      setDescription(updatedShowcase.description);
      setMetaTitle(updatedShowcase.metaTitle);
      setToast('Showcase Saved');
    } else {
      setToast('Something went wrong! Showcase NOT saved');
    }
  };

  const getStepButtonText = () => {
    switch (activeStep) {
      case 0:
        return 'Lineup >';
      case 1:
        return 'Save Showcase';
      default:
        return 'Next';
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <SetupShowcase
            setEditor={setEditor}
            setIsTouched={setIsTouched}
            setVenue={setVenue}
            setDate={setDate}
            setTime={setTime}
            setLocation={setLocation}
            setMetaDesc={setMetaDesc}
            setMetaKeywords={setMetaKeywords}
            setMetaTitle={setMetaTitle}
            venue={venue}
            date={date}
            time={time}
            location={location}
            metaDesc={metaDesc}
            metaKeywords={metaKeywords}
            metaTitle={metaTitle}
            description={description}
          />
        );
      case 1:
        return (
          <AddRemoveArtist
            left={allArtists}
            right={lineUp}
            setRight={setLineUp}
            leftTitle='Wedding Bands'
            rightTitle='LineUp'
          />
        );
      case 2:
        return 'Were done';
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth='lg'>
      <Typography variant='h5' color='primary' gutterBottom>
        Set Showcase
      </Typography>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => {
          return (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box>
        {getStepContent()}
        <Box mt={1}>
          <Button
            disabled={activeStep === 0}
            variant='contained'
            onClick={handleBack}
            sx={{ m: 1 }}
          >
            {'< Back'}
          </Button>
          {activeStep < 2 && (
            <Button
              variant='contained'
              color='primary'
              onClick={handleNext}
              sx={{ m: 1 }}
            >
              {getStepButtonText()}
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
}
